<template>
  <div
    class="metrics-class-view"
    :class="{ 'is-exporting-pdf': isExportingPdf }"
  >
    <metrics-class-header
      :title="currentData.className || null"
      :is-demo-mode="isDemoMode"
      :teacher="
        teacherName ||
        (currentData.classInfo ? currentData.classInfo.teacher : null)
      "
      :students-count="
        currentData.counters ? currentData.counters.students : null
      "
      :assignments-count="
        currentData.counters ? currentData.counters.assignments : null
      "
      :is-page-loading="isPageLoading"
      :show-regenerate="true"
      @update:isDemoMode="onDemoModeChange"
      @regenerate="onRegenerate"
      @export-pdf="exportToPdf"
      @print="print"
    />

    <metrics-class-filters
      :assignments="currentData.assignments"
      :selected-assignments="selectedAssignments"
      :time-filter="timeFilter"
      @update:selectedAssignments="onSelectedAssignmentsChange"
      @update:timeFilter="onTimeFilterChange"
    />

    <div class="metrics-class-view__content">
      <metrics-overall-summary
        v-if="
          currentInsights &&
          currentInsights.summaryTrajectory &&
          currentInsights.summaryTrajectory.overallSummary
        "
        :summary="currentInsights.summaryTrajectory.overallSummary"
      />
      <metrics-grade-distribution
        :overall-avg="currentData.overallAvg"
        :grades="currentData.studentGroups"
        :clickable="hasStudents"
        @tier-click="openStudentsSidebar"
      />

      <metrics-class-charts-row
        :performance-trend-data="performanceTrendData"
        :assignment-summary-data="filteredAssignmentSummary"
        :assignment-base-path="assignmentBasePath"
        :is-performance-trend-loading="isPerformanceTrendLoading"
        :selected-period="selectedPeriod"
        @update:selectedPeriod="onSelectedPeriodChange"
      />

      <metrics-student-trajectories
        :trajectories="studentTrajectoriesData"
        :is-loading="isStudentTrajectoriesLoading"
        :is-exporting-pdf="isExportingPdf"
        :suggestions="
          currentInsights && currentInsights.growthProfiles
            ? currentInsights.growthProfiles.growthProfilesSuggestions
            : null
        "
      />

      <metrics-writing-skills-trajectory
        :insights="currentInsights"
        :is-loading="isClassInsightsLoading"
      />
    </div>
    <metrics-pdf-overlay v-if="isExportingPdf" />

    <students-sidebar
      v-model="studentsSidebarVisible"
      mode="metrics"
      :title="studentsSidebarTitle"
      :metrics-students="studentsSidebarList"
      :is-loading="isStudentTrajectoriesLoading"
      :search-key="studentsSidebarTier"
    />
  </div>
</template>

<script>
import MetricsClassHeader from "./components/class/metrics_class_header.vue";
import MetricsPdfOverlay from "./components/shared/metrics_pdf_overlay.vue";
import MetricsClassFilters from "./components/class/metrics_class_filters.vue";
import MetricsClassChartsRow from "./components/class/metrics_class_charts_row.vue";
import MetricsGradeDistribution from "./components/shared/metrics_grade_distribution.vue";

import MetricsStudentTrajectories from "./components/class/metrics_student_trajectories.vue";
import MetricsOverallSummary from "./components/class/metrics_overall_summary.vue";
import MetricsWritingSkillsTrajectory from "./components/shared/metrics_writing_skills_trajectory.vue";
import StudentsSidebar from "@/components/students/students_sidebar.vue";
import {
  metricsClassesDemoData,
  metricsClassesDemoPerformanceTrends,
  metricsClassDemoData,
  metricsClassEmptyData,
} from "./demo_data/class.js";
import { METRICS_TIME_FILTERS } from "./demo_data/shared.js";
import {
  getMetricsOverallInfo,
  getMetricsPerformanceTrends,
  getMetricsClassAssignmentSummary,
  getMetricsStudentGrowth,
  getMetricsClassInsights,
} from "@/api/requests/metrics.js";
import metricsViewMixin from "@/mixins/metricsViewMixin.js";
import metricsClassViewMixin from "@/mixins/metricsClassViewMixin.js";
import { getScoreTier } from "@/utils/metricsUtils";

const LS_KEY_DEMO_MODE = "metrics-is-demo-mode";
const LS_KEY_TIME_FILTER = "metrics-class-time-filter";

export default {
  name: "MetricsClassView",

  components: {
    MetricsClassHeader,
    MetricsClassFilters,
    MetricsClassChartsRow,
    MetricsGradeDistribution,
    MetricsStudentTrajectories,
    MetricsOverallSummary,
    MetricsWritingSkillsTrajectory,
    MetricsPdfOverlay,
    StudentsSidebar,
  },

  mixins: [metricsViewMixin, metricsClassViewMixin],

  inject: ["workspacesProvider"],

  data() {
    const savedDemo = localStorage.getItem(LS_KEY_DEMO_MODE);
    const savedFilter = localStorage.getItem(LS_KEY_TIME_FILTER);

    return {
      isDemoMode: savedDemo !== null ? JSON.parse(savedDemo) : false,
      timeFilter:
        savedFilter && METRICS_TIME_FILTERS.some((f) => f.value === savedFilter)
          ? savedFilter
          : METRICS_TIME_FILTERS[0].value,
      selectedAssignments: null,
      summaryData: null,
      classInsightsData: null,
      isClassInsightsLoading: false,
      classFolderData: null,
      enteredInDemoMode: false,
      isExportingPdf: false,
      studentsSidebarVisible: false,
      studentsSidebarTier: null,
    };
  },

  computed: {
    classId() {
      return this.$route.params.classId;
    },

    entityParams() {
      if (this.selectedAssignments !== null) {
        return { assignmentIds: this.selectedAssignments };
      }
      return { classIds: [Number(this.classId)] };
    },

    classFolder() {
      return this.classFolderData || this.getFolderFromProvider(this.classId);
    },

    pdfFileName() {
      return "class-overview";
    },

    isPageLoading() {
      return (
        this.isPerformanceTrendLoading ||
        this.isStudentTrajectoriesLoading ||
        this.summaryData === null
      );
    },

    pdfSubjectName() {
      const name = this.classFolder?.name;
      return name ? `(${name})` : "";
    },

    currentInsights() {
      return this.isDemoMode
        ? this.currentData.classInsights
        : this.classInsightsData;
    },

    filteredAssignmentSummary() {
      const all = this.currentData.assignmentSummary || [];
      if (this.selectedAssignments === null) return all;
      return all.filter((a) => this.selectedAssignments.includes(a.id));
    },

    assignmentBasePath() {
      return `${this.metricsBasePath}/class/${this.classId}`;
    },

    studentsSidebarTitle() {
      const TIER_KEYS = {
        high: "metrics.tierSidebarHigh",
        medium: "metrics.tierSidebarMedium",
        low: "metrics.tierSidebarLow",
      };
      const key = TIER_KEYS[this.studentsSidebarTier];
      return key ? this.$t(key) : this.$t("metrics.tierSidebarDefault");
    },

    hasStudents() {
      return (this.studentTrajectoriesData?.data?.length || 0) > 0;
    },

    studentsSidebarList() {
      const students = this.studentTrajectoriesData?.data || [];
      if (!this.studentsSidebarTier) return students;
      return students.filter(
        (student) => getScoreTier(student.avgGrade) === this.studentsSidebarTier
      );
    },

    currentData() {
      if (this.isDemoMode) {
        return (
          metricsClassesDemoData[Number(this.classId)] || metricsClassDemoData
        );
      }
      const base = this.summaryData || metricsClassEmptyData;
      return {
        ...base,
        className: this.classFolder?.name || base.className || null,
      };
    },
  },

  mounted() {
    this.enteredInDemoMode = this.isDemoMode;
    if (this.isDemoMode) {
      this.fetchPerformanceTrend();
      this.fetchStudentGrowth();
    } else {
      this.fetchSummary();
      this.fetchClassInsights();
      this.loadClassFolder();
    }
  },

  methods: {
    onRegenerate() {
      this.summaryData = null;
      this.classInsightsData = null;
      this.fetchSummary();
      this.fetchClassInsights();
    },

    openStudentsSidebar(tier) {
      this.studentsSidebarTier = tier;
      this.studentsSidebarVisible = true;
    },

    async loadClassFolder() {
      const folder = await this.fetchEntityFolder(this.classId);
      if (folder) this.classFolderData = folder;
    },

    onDemoModeChange(value) {
      this.isDemoMode = value;
      localStorage.setItem(LS_KEY_DEMO_MODE, JSON.stringify(value));
      if (!value) {
        if (this.enteredInDemoMode) {
          this.$router.push(this.metricsOverviewPath);
          return;
        }
        this.summaryData = null;
        this.classInsightsData = null;
        this.performanceTrendData = { labels: [], datasets: [] };
        this.studentTrajectoriesData = { summary: {}, data: [] };
        this.fetchSummary();
        this.fetchClassInsights();
      } else {
        this.fetchPerformanceTrend();
        this.fetchStudentGrowth();
      }
    },

    onTimeFilterChange(value) {
      this.timeFilter = value;
      localStorage.setItem(LS_KEY_TIME_FILTER, value);
      const filter = METRICS_TIME_FILTERS.find((f) => f.value === value);
      if (filter) {
        this.selectedPeriod = filter.getParams().period;
      }
      this.fetchSummary();
    },

    onSelectedAssignmentsChange(value) {
      this.selectedAssignments = value;
      this.fetchSummary();
    },

    async fetchPerformanceTrend() {
      if (!this.classId) return;
      if (this.isDemoMode) {
        const classTrends =
          metricsClassesDemoPerformanceTrends[Number(this.classId)] ||
          metricsClassesDemoPerformanceTrends[1];
        this.performanceTrendData =
          classTrends[this.selectedPeriod] || classTrends.month;
        return;
      }
      const allIds = this.currentData.assignments.map((a) => a.id);
      const assignmentIds = this.selectedAssignments ?? allIds;
      if (!assignmentIds.length) {
        this.performanceTrendData = { labels: [], datasets: [] };
        return;
      }
      const trendParams =
        this.selectedAssignments !== null
          ? { assignmentIds: this.selectedAssignments }
          : { classIds: [Number(this.classId)], assignmentIds };
      this.isPerformanceTrendLoading = true;
      try {
        const filter = METRICS_TIME_FILTERS.find(
          (f) => f.value === this.timeFilter
        );
        const timeParams = filter ? filter.getParams() : {};
        const result = await getMetricsPerformanceTrends({
          entityId: this.entityId,
          ...trendParams,
          dateFrom: timeParams.dateFrom,
          dateTo: timeParams.dateTo,
          period: this.selectedPeriod,
        });
        this.performanceTrendData = result
          ? result.data
          : { labels: [], datasets: [] };
      } catch {
        this.performanceTrendData = { labels: [], datasets: [] };
      } finally {
        this.isPerformanceTrendLoading = false;
      }
    },

    async fetchAssignmentSummary() {
      if (!this.classId) return;
      if (this.isDemoMode) {
        this.fetchPerformanceTrend();
        this.fetchStudentGrowth();
        return;
      }
      try {
        const filter = METRICS_TIME_FILTERS.find(
          (f) => f.value === this.timeFilter
        );
        const timeParams = filter ? filter.getParams() : {};
        const result = await getMetricsClassAssignmentSummary({
          entityId: this.entityId,
          classIds: [Number(this.classId)],
          dateFrom: timeParams.dateFrom,
          dateTo: timeParams.dateTo,
          limit: 50,
        });
        if (result?.data?.data) {
          const assignmentsList = result.data.data;
          this.summaryData = {
            ...(this.summaryData || {}),
            assignments: assignmentsList.map(({ id, name }) => ({ id, name })),
            assignmentSummary: assignmentsList,
          };
        }
      } catch {
        // assignment summary failed, proceed to trend with existing assignments
      }
      this.fetchStudentGrowth();
      this.fetchPerformanceTrend();
    },

    async fetchStudentGrowth() {
      if (!this.classId) return;
      if (this.isDemoMode) {
        const classData =
          metricsClassesDemoData[Number(this.classId)] || metricsClassDemoData;
        this.studentTrajectoriesData = classData.studentTrajectories;
        return;
      }
      const allIds = this.currentData.assignments.map((a) => a.id);
      const assignmentIds = this.selectedAssignments ?? allIds;
      if (!assignmentIds.length) {
        this.studentTrajectoriesData = { summary: {}, data: [] };
        return;
      }
      const growthParams =
        this.selectedAssignments !== null
          ? { assignmentIds: this.selectedAssignments }
          : { classIds: [Number(this.classId)], assignmentIds };
      this.isStudentTrajectoriesLoading = true;
      try {
        const filter = METRICS_TIME_FILTERS.find(
          (f) => f.value === this.timeFilter
        );
        const timeParams = filter ? filter.getParams() : {};
        const result = await getMetricsStudentGrowth({
          entityId: this.entityId,
          ...growthParams,
          dateFrom: timeParams.dateFrom,
          dateTo: timeParams.dateTo,
        });
        this.studentTrajectoriesData = result?.data || {
          summary: {},
          data: [],
        };
      } catch {
        this.studentTrajectoriesData = { summary: {}, data: [] };
      } finally {
        this.isStudentTrajectoriesLoading = false;
      }
    },

    async fetchSummary() {
      if (!this.classId || this.isDemoMode) return;
      const filter = METRICS_TIME_FILTERS.find(
        (f) => f.value === this.timeFilter
      );
      const timeParams = filter ? filter.getParams() : {};
      try {
        const summaryResult = await getMetricsOverallInfo({
          entityId: this.entityId,
          ...this.entityParams,
          dateFrom: timeParams.dateFrom,
          dateTo: timeParams.dateTo,
        });
        if (summaryResult?.data) {
          this.summaryData = {
            ...summaryResult.data,
            classInfo: { teacher: null },
            overallSummary: null,
            assignments: this.summaryData?.assignments || [],
            assignmentSummary: this.summaryData?.assignmentSummary || [],
          };
        } else {
          this.summaryData = null;
        }
      } catch {
        this.summaryData = null;
      }
      this.fetchAssignmentSummary();
    },

    async fetchClassInsights() {
      if (!this.classId || !this.entityId || this.isDemoMode) return;
      this.isClassInsightsLoading = true;
      try {
        const result = await getMetricsClassInsights({
          entityId: this.entityId,
          classId: this.classId,
        });
        this.classInsightsData = result?.data || null;
      } catch {
        this.classInsightsData = null;
      } finally {
        this.isClassInsightsLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./components/shared/metrics_card";

.metrics-class-view {
  @include metrics-view;
  gap: 16px;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 24px;
  }
}
</style>
